/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeEvent, useEffect, useState } from 'react';

import { ApolloError } from '@apollo/react-hooks';
import { IDirectory, IUseSearchDirectoriesOnCompletedResponse, Reveal, useSearchDirectories } from '@netfront/ekardo-content-library';
import { GoogleMap, Breadcrumbs, IBreadcrumbItem } from '@netfront/ui-library';
import kebabCase from 'lodash.kebabcase';
import { useRouter } from 'next/router';
import Pluralize from 'react-pluralize';

import { Affiliates } from '../../components/Affiliates';
import { BreadcrumbItemHomePageLink, BreadcrumbItem } from '../../components/BreadcrumbItems';
import { Button } from '../../components/Button';
import { DirectoryHeader, DIRECTORY_TYPE_IDS, getMarkers, SYDNEY_LOCATION } from '../../components/Directory';
import { HintBox } from '../../components/HintBox';
import { HoverLink } from '../../components/HoverLink';
import { PinIcon } from '../../components/Icons';
import { Link } from '../../components/Link';
import { PageLayout } from '../../components/PageLayout';
import { WarningBox } from '../../components/WarningBox';
import { useToast } from '../../hooks/useToast';

const PAGE_TITLE = 'Directory';

const BREADCRUMB_ITEMS: IBreadcrumbItem[] = [
  {
    content: <BreadcrumbItemHomePageLink />,
    key: 'home',
  },
  {
    content: <BreadcrumbItem text={PAGE_TITLE} />,
    key: kebabCase(PAGE_TITLE),
  },
];

const RADIUS = Object.freeze({
  max: 200,
  min: 20,
  step: 20,
});

const DirectoryPage = () => {
  const { push } = useRouter();
  const { handleToastError } = useToast();

  const [currentDirectories, setCurrentDirectories] = useState<IDirectory[]>([]);
  const [isUsingMyLocation, setIsUsingMyLocation] = useState<boolean>(false);
  const [postcodeFilter, setPostcodeFilter] = useState<string>();
  const [radius, setRadius] = useState<number>(RADIUS.min);

  const handleDirectoryItemClick = (friendlyUrl: string) => {
    push('/directory/[serviceFriendlyUrl]', `/directory/${String(friendlyUrl)}`).catch((error) =>
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      }),
    );
  };

  const handlePostcodeFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    setPostcodeFilter(value);
  };

  const handlePostcodeFilterClick = () => {
    if (!postcodeFilter) {
      handleSearchDirectories({
        directoryTypeId: DIRECTORY_TYPE_IDS.services,
      });

      return;
    }

    handleSearchDirectories({
      directoryTypeId: DIRECTORY_TYPE_IDS.services,
      postcodeGeolocalisation: {
        country: 'AU',
        postcode: `postcode ${postcodeFilter}`, // Note: Hack to get Google to recognize this as a postcode
        radius: Number(radius),
      },
    });
  };

  const handleRadiusChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    setRadius(Number(value));
  };

  const handleSearchDirectoriesCompleted = (data: IUseSearchDirectoriesOnCompletedResponse): void => {
    const { directories } = data;

    const filteredDirectories = directories.filter(({ friendlyUrl }) => friendlyUrl);

    setCurrentDirectories(filteredDirectories);
  };

  const handleSearchDirectoriesError = (error: ApolloError): void => {
    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const handleUseMyLocationClick = () => {
    setIsUsingMyLocation((currentValue) => !currentValue);
  };

  const handleUseMyLocationClickCompleted = (position: GeolocationPosition) => {
    const {
      coords: { latitude, longitude },
    } = position;

    setIsUsingMyLocation((currentValue) => !currentValue);

    handleSearchDirectories({
      directoryTypeId: DIRECTORY_TYPE_IDS.services,
      geoPositionGeolocalisation: {
        latitude,
        longitude,
        radius: Number(radius),
      },
    });
  };

  const { handleSearchDirectories, isLoading } = useSearchDirectories({
    onCompleted: handleSearchDirectoriesCompleted,
    onError: handleSearchDirectoriesError,
  });

  useEffect(() => {
    handleSearchDirectories({
      directoryTypeId: DIRECTORY_TYPE_IDS.services,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isUsingMyLocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(handleUseMyLocationClickCompleted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsingMyLocation]);

  const totalDirectories = currentDirectories.length;

  return (
    <PageLayout isPreloaderVisible={isLoading ?? isUsingMyLocation} title={PAGE_TITLE}>
      <div className="container-md flex justify-end py-8">
        <Breadcrumbs items={BREADCRUMB_ITEMS} />
      </div>

      <DirectoryHeader subHeading="Directory" />

      <section className="container-md pb-8">
        <HintBox>
          <h3 className="h5 color-accent mb-6 weight-800">Welcome to the CarerWell National Brain Cancer Service Directory</h3>
          <p>The National CarerWell Service Directory provides information about:</p>
          <ul className="list-disc">
            <li className="mb-2">specialist brain cancer care services and providers</li>
            <li className="mb-2">services that provide general brain cancer care such as general practices and allied health</li>
            <li>
              organisations and community support agencies who provide services to people living with brain cancer and/or their
              carers/friends and family members.
            </li>
          </ul>
        </HintBox>
      </section>

      <section className="container-md pb-4">
        <WarningBox>
          <p className="mb-0">
            If this is an emergency, please call triple zero (000) or use the CarerWell &lsquo;Get Help Now&rsquo; button
          </p>
        </WarningBox>
      </section>

      <section className="container-md">
        <Reveal
          backgroundColor="#3a8fa2"
          borderColor="transparent"
          eventDirection=""
          eventTitle="Purpose of the Directory"
          iconUrl=""
          style="TEXT_AND_ICON"
          titleColor="#fff"
          triggerStyle="ARROW"
        >
          <div className="p-4">
            <p>
              The purpose of the Directory is to provide consistent, reliable and easily accessible information about brain cancer services
              and support to the Australian public. The Directory may not provide a complete list of services and support that may be
              available to you, and it is important that you discuss health and support needs with the treatment and care team.
            </p>
            <p>The Directory provides information which includes:</p>
            <ul className="list-disc mb-4">
              <li className="mb-2">name and type of service</li>
              <li className="mb-2">location and contact details</li>
              <li className="mb-2">description of service</li>
              <li className="mb-2">ability to browse available services in your local area</li>
            </ul>
            <p>
              If you have any questions about this information, or believe the information is incomplete or out of date, please contact the
              University of Newcastle via email{' '}
              <Link className="color-accent" href="mailto:CarerWellProgram@newcastle.edu.au">
                CarerWellProgram@newcastle.edu.au
              </Link>
            </p>
          </div>
        </Reveal>
      </section>

      <div className="container-md pt-8 pb-12">
        <hr className="border-none bg-white h-1 radius-full" />
      </div>

      <section className="container-md pb-8">
        <div className="flex flex-col md:flex-row -mx-8">
          <div className="w-full md:w-2/5 mb-8 md:mb-4 px-8">
            <Link href="/directory/add-service" theme="default">
              <svg className="mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
              Register your service
            </Link>
          </div>
          <div className="align-end flex flex-col justify-end md:w-3/5 px-8 w-full">
            <div className="align-end flex mb-4 w-full">
              <input
                className="w-full"
                placeholder="Search postcode"
                type="text"
                value={postcodeFilter}
                onChange={handlePostcodeFilterChange}
              />
              <Button aria-label="Search resources" className="min-w-auto ml-2" theme="red" onPress={handlePostcodeFilterClick}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                </svg>
              </Button>
            </div>
            <div className="flex flex-1 w-full">
              <div className="align-center flex flex-1">
                <span className="pt-2 mr-2 weight-700">Radius</span>
                <input
                  id="radius"
                  max={RADIUS.max}
                  min={RADIUS.min}
                  name="radius"
                  step={RADIUS.step}
                  type="number"
                  value={radius}
                  onChange={handleRadiusChange}
                />
                <span className="pt-2 ml-2 weight-700">km</span>
              </div>
              <div className="flex">
                <Button className="flex align-center focus:border-accent pointer" onPress={handleUseMyLocationClick}>
                  <div className="color-accent">
                    <PinIcon />
                  </div>
                  <span className="font-header weight-700 color-blue-800 ml-2 mb-0">Use my location</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-md pb-12">
        <h3 className="h6 mb-6 pl-4 weight-800">
          Displaying <span className="color-accent h6 weight-800">{totalDirectories}</span>{' '}
          <Pluralize className="h6 mb-4 weight-800" count={totalDirectories} showCount={false} singular="result" />
        </h3>
        <div className="flex flex-col md:flex-row -mx-8">
          <div className="c-directory__results">
            {totalDirectories ? (
              <ul className="c-directory__results-list">
                {currentDirectories.map(({ friendlyUrl, id, title }) => (
                  <li key={`directory-item-${id}`}>
                    <HoverLink label={title} onClick={() => handleDirectoryItemClick(String(friendlyUrl))} />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="md:w-3/4 px-8 w-full">
            <GoogleMap
              location={SYDNEY_LOCATION}
              markers={getMarkers(currentDirectories).map(({ latitude: lat, longitude: lng }) => ({
                lat,
                lng,
              }))}
              zoomScale={8}
              isDraggable
            />
          </div>
        </div>
      </section>

      <section className="container-md pb-12">
        <small>
          Disclaimer: This directory is designed for information purposes only. CarerWell gives no warranty and accepts no responsibility
          for the accuracy or completeness of the information. CarerWell does not necessarily endorse or recommend the professionals and
          services listed here. Although we strive to keep the directory up to date, no guarantee is given about the relevance and accuracy
          of the listings provided, no user should rely on the information, but instead should check for confirmation with the originating
          body. We do not warrant that that the Services will conform to any particular service levels or performance specifications or that
          you will be able to access a particular service or support.
        </small>
      </section>
      <Affiliates />
    </PageLayout>
  );
};

// eslint-disable-next-line import/no-default-export
export default DirectoryPage;
